/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 12:03:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import { schemaBuilder, Many2OneIntPKBaseSchema } from "lib/validation";
import { WritableModel } from "lib/schemas/abstract";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  DesignLayout,
  NumberingSequence,
} from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";

export enum DOCUMENT_TYPE_OPTIONS {
  // invoice = "Invoice", - Deprecated since OneStop v1.4.0
  permit = "Permit",
  // payment_receipt = "Payment Receipt", - Deprecated since OneStop v1.4.0
  other = "Other",
}

export enum SHOW_COPY_ONLY_TO_OPTIONS {
  client = "Clients",
  staff = "Staff",
  all = "Both Client and Staff",
  unsigned = "When Document is Unsigned",
  unsigned_or_client = "When Document is Unsigned or User is Client",
}

export enum DOWNLOAD_ACTION_BEFORE_SIGNING_OPTIONS {
  no_download = "No Downloading",
  no_download_staff = "No Downloading for staff",
  no_download_client = "No Downloading Clients",
}

export enum DOCUMENT_NUMBERING_TYPES {
  sequence = "Use a Configured Numbering Sequence",
  // serial = "Use an incremental Serial Number",
  submission_reference = "Use the Submission Reference if available",
}

export interface DocumentTemplate extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  document_type: keyof typeof DOCUMENT_TYPE_OPTIONS;
  sequence?: PrimaryKeyValue<NumberingSequence> & number;
  layout: PrimaryKeyValue<DesignLayout> & number;
  qr_data_layout?: PrimaryKeyValue<DesignLayout> & number;
  list_layout?: PrimaryKeyValue<DesignLayout> & number;
  valid_for: number;
  show_copy_only_to?: keyof typeof SHOW_COPY_ONLY_TO_OPTIONS;
  document_copy_template?: PrimaryKeyValue<DocumentTemplate> & number;
  one_valid_template?: boolean;
  download_action_before_signing?: DOWNLOAD_ACTION_BEFORE_SIGNING_OPTIONS;
  readonly is_dangling?: boolean;
  enable_document_numbering?: boolean;
  document_numbering_type?: keyof typeof DOCUMENT_NUMBERING_TYPES;
}

type MutableDocumentTemplate = Omit<DocumentTemplate, "is_dangling">;

export const DocumentTemplateValidationSchema: ProcessTemplateArtifactSchemaType<MutableDocumentTemplate> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.number().positive().nullable(),
    title: schemaBuilder.string().required("Please provide a title for the document template"),
    document_type: schemaBuilder
      .mixed()
      .oneOf(Object.keys(DOCUMENT_TYPE_OPTIONS))
      .required("Document type is required"),
    sequence: Many2OneIntPKBaseSchema().optional().nullable(),
    layout: Many2OneIntPKBaseSchema().required("A layout is required for this document template"),
    qr_data_layout: Many2OneIntPKBaseSchema().optional().nullable(),
    list_layout: Many2OneIntPKBaseSchema().optional().nullable(),
    valid_for: schemaBuilder.number().default(0),
    show_copy_only_to: schemaBuilder
      .mixed()
      .oneOf([null, ...Object.keys(SHOW_COPY_ONLY_TO_OPTIONS)])
      .optional(),
    document_copy_template: schemaBuilder.mixed().optional().nullable(),
    one_valid_template: schemaBuilder.boolean().optional().nullable(),
    download_action_before_signing: schemaBuilder
      .mixed()
      .oneOf([null, ...Object.keys(DOWNLOAD_ACTION_BEFORE_SIGNING_OPTIONS)])
      .optional(),
    enable_document_numbering: schemaBuilder.boolean().optional().nullable(),
    document_numbering_type: schemaBuilder
      .mixed()
      .oneOf([null, ...Object.keys(DOCUMENT_NUMBERING_TYPES)])
      .optional(),
  });
